import { ApiError, TransportError } from '../api/errors'
import type { AppLocale } from './numerals'

/* ============================================================================
   errorMessages.ts — the error catalogue, keyed on the server's codes.

   THE CODE IS THE KEY. The server sends "room.transition.reasonRequired"; that string
   is both what the client branches on and what it looks up. One vocabulary, so the two
   cannot drift.

   Placeholders are NAMED — {roomNumber}, not %s or {0}. Arabic and English do not
   order their clauses the same way, so a positional placeholder forces the Arabic
   translator to either reorder the sentence badly or leave it in English word order.
   A named one can be moved anywhere in the string.

   The catalogue is asserted COMPLETE against the server's own list of codes
   (/api/v1/meta/error-codes, checked in as errorCodes.ts) in errorMessages.test.ts.
   A new server code with no translation fails the build, not a user's morning.
   ========================================================================= */

type Catalogue = Record<string, string>

const en: Catalogue = {
  'room.notFound':
    'Room {roomNumber} could not be found. It may have been removed since this screen loaded.',
  /* M-17. SAID AS THE NEXT ACTION, not as a fault: scanning is a shortcut and the list is
     always there. An attendant who reads this should put the phone down, not go looking for
     a supervisor. */
  'room.doorCode.notRecognised':
    "That is not a door code from this hotel. Find the room in your list instead — "
    + 'scanning is only a shortcut.',
  'room.doorCode.replaced':
    'The card on this door has been replaced, so the old code no longer works. '
    + 'Use your list, and tell your supervisor the old card is still up.',
  'room.transition.notPermitted':
    'Room {roomNumber} cannot go from {fromState} to {toState}.',
  'room.transition.reasonRequired':
    'This change needs a reason before it can be recorded.',
  'room.changedConcurrently':
    'Somebody else changed room {roomNumber} while this screen was open. Refresh to see the current state.',

  'task.notFound':
    'That task could not be found. It may have been completed or cancelled.',
  'task.state.notPermitted':
    'That task cannot move to {toState} from where it is now.',
  'task.notAssigned':
    'That task is not assigned to anyone yet.',

  /* ── RAISING AND CANCELLING WORK (Sprint 33) ────────────────────────────── */

  /* A FORM MESSAGE, NOT A DEVELOPER'S ONE. The CHECK constraint says the same thing in
     the language of the schema; a person filling in a form needs to be told which of the
     two boxes to use. */
  'task.needsRoomXorArea':
    'Choose either a room or a public area for this work — one or the other, not both.',
  /* THE CODE THEY SENT, because a stale list is the commonest cause and seeing the word
     is what makes somebody refresh the page rather than report a fault. */
  'task.typeUnknown':
    'This hotel has no kind of work called “{value}”. Refresh the page and choose again.',
  /* WHERE TO GO NEXT. The room board is where a room's availability is changed, and a
     supervisor told only "no" goes looking on the wrong screen. */
  'task.roomNotInService':
    'Room {roomNumber} is not in service, so nobody can be sent to clean it. Put it back '
    + 'in service on the room board first.',
  /* THE CONSEQUENCE, NOT THE FIELD. "A reason is required" is a form telling somebody off;
     this says why anybody would want one. */
  'task.cancel.reasonRequired':
    'Say why this work is being cancelled. Whoever was given the room will want to know, '
    + 'and so will anyone reading the record in six weeks.',
  /* THE RANGE IN THE FORM A PERSON READS A CLOCK IN. "Between 0 and 1439" is true and
     would be answered by nobody; a supervisor is thinking "half past five". */
  'work.schedule.minuteOutOfRange':
    'That is not a time of day. Choose any time between 00:00 and 23:59, or turn the '
    + 'schedule off to go back to generating the day by hand.',
  'task.inspection.selfForbidden':
    'You cannot inspect work you carried out yourself. A different supervisor needs to inspect this room.',
  'task.inspection.reasonRequired':
    'A rejected inspection needs a reason, so the attendant knows what to put right.',
  'task.assigneeNotOnShift':
    'That person is not working today, so work cannot be given to them. Check the roster, or choose somebody on shift.',
  /* A PAIR, AND SO A DIFFERENT SENTENCE. Either man being in is enough for the floor to
     take work, so this refusal means NEITHER is — and telling a supervisor "that person"
     would send him looking for one of two people, neither of whom is in the building. */
  'task.teamNotOnShift':
    'Neither of them is working today, so this room cannot go to that pair. Choose somebody on shift, or record a stand-in first.',
  /* W-127's rule, and the message has one job: send the reader to the screen that
     fixes it. "Cannot be assigned" would send a supervisor looking for a different
     attendant, and no attendant would have worked.

     AGREEMENT-FREE, because the catalogue is one message per code with no plural
     machinery (#92) — "its checklists and cleaning time" rather than a count of what is
     missing. The missing rules travel in the details for the screen to highlight.

     **"CREDITS" UNTIL SPRINT 33.** The word was left behind when the model became
     minutes: it sent a supervisor to W-127 looking for a credits box that no longer
     exists on it. Found while unifying the Arabic (#115) — the two languages had drifted
     apart in the same sentence, one of them out of date in English. */
  'task.room.noCleaningRules':
    'Room {roomNumber} has no cleaning rules set, so it cannot be given to anyone yet. '
    + 'Set its checklists and cleaning time under Rooms and areas.',
  /* M-21. NOT "that move is not allowed" — the advice is to go and find the supervisor,
     because the figures have stopped being his and started being hers. */
  'task.linen.recordingClosed':
    'This room has been handed in, so the linen figures are your supervisor\'s to change '
    + 'now. Tell them what was different.',
  'task.linen.reasonRequired':
    'Changing a linen figure somebody else recorded needs a reason.',
  'task.linen.itemNotOnStandard':
    'That is not something this kind of room needs, so there is nowhere to record it.',
  'trolley.notAssigned':
    'You have no trolley today. Ask your supervisor which one is yours.',
  'trolley.itemNotCarried':
    'That is not something the trolley is counted for, so there is nowhere to record it.',
  'trolley.quantityOutOfRange':
    'That is more than a trolley can hold. Check the number and try again.',
  'task.checklist.notFound':
    'This kind of task has no checklist set up.',
  'task.checklist.itemNotOnThisList':
    'That item is not on this version of the checklist. Reload the room to get the current list.',
  'task.checklist.exceptionNeedsNote':
    'Say what stopped you before marking this as an exception.',
  'task.checklist.incomplete':
    '{outstandingCount} things on the checklist still need an answer before this room can be submitted.',
  /* A DIFFERENT SENTENCE, because it needs different advice. "You missed two items"
     sends an attendant back through a list where everything is already ticked. */
  /* AGREEMENT-FREE ON PURPOSE. The catalogue has no plural machinery — it is one
     message per code — so the sentence is shaped so that "1" and "3" both read
     correctly, rather than being right for the rarer case. */
  'task.checklist.photographRequired':
    'A photograph is needed for {count} of the things you ticked before this room can '
    + 'be submitted. Open the item and use the camera.',

  /* ── sign-in ────────────────────────────────────────────────────────────
     Three answers, because the person to call is different each time. Collapsing
     them into "could not sign in" is how somebody spends a morning phoning IT
     about a problem only their own administrator can fix. */
  'auth.notSignedIn':
    'You are not signed in. Sign in again to continue.',
  'auth.userNotFound':
    'This account is no longer active here. Your administrator can restore it.',
  'auth.assertionRejected':
    'Sign-in was not accepted. Try again, and tell IT if it keeps happening.',
  'auth.noAccount':
    'You signed in, but employee number {employeeId} has no roles here yet. '
    + 'An administrator needs to assign one before you can start.',

  /* ── issues and guest requests ──────────────────────────────────────── */
  'service.valueUnknown':
    /* ONE placeholder, not one per field. It was `({severity}{toState}{channel})`
       until it was read aloud: only one is ever supplied, so the message rendered as
       "(BLOCKING{toState}{channel})" — three quarters of it literal braces in front of
       somebody. The server names the offending value under a single `value` key. */
    'This version of the app sent a value the server does not know ({value}). Update the app.',
  'issue.notFound':
    'That report no longer exists. It may have been closed by somebody else.',
  'issue.state.notPermitted':
    'A report that is {from} cannot be moved to {to}.',
  'issue.reasonRequired':
    'Say why before closing this report — the person who raised it will see it.',
  'request.notFound':
    'That request no longer exists. It may have been closed by somebody else.',
  'request.state.notPermitted':
    'A request that is {from} cannot be moved to {to}.',
  'request.reasonRequired':
    'Say why this is being declined — the guest is being told no.',

  'auth.stagingKeyRequired':
    'This is the staging server, and it needs the access key your administrator gave you. '
    + 'It is not your password — everybody testing uses the same one.',
  'auth.sessionExpired':
    'Your session has ended. Sign in again to carry on — anything you had queued is still there.',

  /* ── reports ────────────────────────────────────────────────────────── */
  'report.notFound':
    'That report does not exist on this server. Reload the list.',
  'report.period.invalid':
    'The end of the period is before the start ({from} to {to}).',
  'report.period.tooLong':
    'A report can cover at most {maxDays} days. Choose a shorter period.',
  /* NOT "the report failed". It did not — it can be read on screen; only the printing
     is unavailable, and saying otherwise turns a server-configuration problem into a
     bug report about reporting. */
  'report.pdfUnavailable':
    'This server cannot make PDFs — no browser is installed for it. '
    + 'You can still read the report on screen. Ask whoever set the server up.',

  /* ── loading the hotel's own rooms (W-127) ──────────────────────────────
     THESE ARE READ AGAINST A LINE NUMBER, in a table, by somebody who has a spreadsheet
     open beside them. Every one of them names what to change and where — "invalid data"
     against 500 rows is a message that produces a telephone call, not a corrected file. */
  'load.noRows':
    'There are no rooms in that file.',
  'load.tooManyRows':
    'That file has {value} rows in it, and the most that can be loaded at once is '
    + '{other}. A file that size is usually an export of something other than rooms.',
  'load.missingColumn':
    'That file has no “{value}” column. The first row should name the columns — room, '
    + 'floor and type are the three it cannot be read without.',
  'load.notASpreadsheet':
    'That file could not be opened as a spreadsheet. Save it as .xlsx and try again.',
  /* THE ARABIC ENCODING TRAP. Excel's plain "Save as CSV" writes Arabic in the system
     code page, and importing it would fill the database with replacement characters
     somebody finds six weeks later. Named, so the fix is one menu away. */
  'load.textEncodingWrong':
    'That file is not saved in a way that keeps Arabic text. In Excel choose '
    + '“CSV UTF-8” when saving, or send the .xlsx file instead.',
  'load.fileTooLarge':
    'That file is too big to be a room list. Send just the sheet of rooms.',
  'load.roomNumberMissing':
    'No room number on this line.',
  'load.buildingMissing':
    'No building on this line. The file names a building on its other rows, so leaving '
    + 'this one blank would put the room in whichever building somebody guessed.',
  /* THE FILE-LEVEL ONE, and it is the reason the column exists. Four towers of four
     floors each means “floor 3” names four different places. */
  'load.buildingColumnRequired':
    'This hotel has {value} buildings ({other}), so the file needs a Building column — '
    + 'without one there is no way to tell which building a floor is in.',
  'load.floorMissing':
    'No floor on this line.',
  'load.typeMissing':
    'No room type on this line.',
  'load.valueTooLong':
    '“{value}” is too long to store.',
  'load.cleanMinutesNotANumber':
    '“{value}” is not a number of minutes.',
  'load.cleanMinutesOutOfRange':
    '{value} minutes is outside what a clean can take. Use a number between 5 and 240.',
  'load.capacityNotANumber':
    '“{value}” is not a number of people.',
  /* 0 is a room somebody has not finished describing; 404 is a room number typed into the
     capacity cell, which is what the upper bound is really for. */
  'load.capacityOutOfRange':
    'A room cannot sleep {value} people. Use a number between 1 and 12 — this hotel’s '
    + 'rooms sleep 2 or 4.',
  'load.capacityConflict':
    'Room type {value} sleeps a different number of people here from line {other}. One '
    + 'code, one size — if these are two different rooms they need two codes.',
  'load.duplicateRoom':
    'Room {value} is already on line {other} of this file. Delete one of them.',
  'load.typeNameConflict':
    'Room type {value} is named differently here from line {other}. One code, one name.',
  'load.fileHasProblems':
    'That file still has {problems} to put right, so nothing was loaded. Fix them and '
    + 'upload it again — it is all or nothing, so that the hotel is never half loaded.',

  /* ── editing one room (W-127's other half) ──────────────────────────────
     Read by an administrator sitting in an office with the hotel's floor plan, not by a
     housekeeper on a landing — so these name the next action rather than the rule that
     was broken. "That number is taken" sends somebody looking; "306 is out of the hotel,
     put it back" is what they were going to have to do anyway. */
  'room.numberInUse':
    'There is already a room {roomNumber} in this hotel.',
  'room.numberHeldByRemovedRoom':
    'Room {roomNumber} exists but is out of the hotel, and it still holds that number. '
    + 'Put it back rather than giving its number to a second room.',
  'room.numberInvalid':
    '“{value}” cannot be used as a room number.',
  'room.floorNotFound':
    'That floor is not one of this hotel’s floors. Refresh and choose again.',
  'room.buildingNotFound':
    'That building is not one of this hotel’s buildings. Refresh and choose again.',

  /* ── teams of two ───────────────────────────────────────────────────────
     Read by a supervisor at the start of a shift, with a man standing in front of him.
     Each names what to do next, because "invalid team" would send him to look at the
     one thing on the screen that is already right. */
  'team.notFound':
    'That team no longer exists. Refresh the screen — somebody may have broken up the pair.',
  'team.samePersonTwice':
    'A team is two different men. Choose somebody else for the second place.',
  'team.memberAlreadyPaired':
    '{name} is already on a team. Take him off that one first, or choose somebody who is not paired.',
  'team.standInAlreadyOnTeam':
    '{name} is already on this team, so he cannot cover for his own partner. '
    + 'Choose somebody from another floor.',
  'team.notOnThisTeam':
    '{name} is not on this team. The pair may have changed while this was open — refresh and try again.',
  'room.typeNotFound':
    'That room type is not one of this hotel’s types. Refresh and choose again.',
  /* A TYPE CALLED "TWN" ON EVERY SCREEN IS ONE NOBODY EVER GOES BACK AND FIXES, which is
     why falling back to the code is refused rather than done quietly. */
  'room.typeNameRequired':
    'Give {value} a name. The code is what reports count by; the name is what the staff '
    + 'read on every card.',
  'room.typeCapacityInvalid':
    'A room cannot sleep {value} people. Use a number between 1 and 12 — this hotel’s '
    + 'rooms sleep 2 or 4.',
  'room.removal.reasonRequired':
    'Say why this room is leaving the hotel. Somebody will ask in six weeks’ time.',
  'room.restore.reasonRequired':
    'Say why this room is coming back. It goes on sale again as soon as it does.',
  'room.alreadyRemoved':
    'Room {roomNumber} is already out of the hotel.',
  /* ── CQ-138: taking a room off sale by hand ────────────────────────────────
     **THE SENTENCE SAYS WHY IT IS BEING ASKED FOR, NOT THAT A FIELD IS EMPTY.** A
     supervisor who is told "reason required" types a full stop; one who is told somebody
     will ask in a fortnight types the carpet. */
  'room.service.reasonRequired':
    'Say why. A room off sale with no reason against it is one nobody dares put back.',
  'room.service.unchanged':
    'Room {roomNumber} is already like that — somebody may have just done it.',
  /* NOT "you may not". He may; this is not the door. A fault took the room out of order
     and the room comes back by resolving the fault, so the message names the screen. */
  'room.isOutOfOrder':
    'Room {roomNumber} is out of order because a fault was raised on it. Resolve the '
    + 'fault to put it back on sale.',
  'room.notRemoved':
    'Room {roomNumber} is in the hotel already.',
  /* NOT "the room is busy". The point is that a person has been sent to it and the fix
     is on a different screen, so the message says where to go. */
  'room.hasWorkInHand':
    'Somebody has been sent to room {roomNumber} — there is work on it that has not '
    + 'finished. Cancel it on the assignment board first, then take the room out.',
  /* The number is the hotel's own arithmetic, not a setting — so the message says what
     the two ends of the range mean rather than only naming them. */
  'property.minutesPerCredit.outOfRange':
    '{minutes} minutes cannot be one credit. Use a number between 5 and 120 — below that '
    + 'a single room is worth a whole shift, above it no room is worth a credit at all.',
  'building.codeInUse':
    'This hotel already has a building “{value}”.',
  'building.codeInvalid':
    '“{value}” cannot be used as a building.',
  /* NAMED AGAINST THE BUILDING, because a hotel of four towers has four third floors and
     "this hotel already has a floor 3" would be a refusal nobody can act on. */
  'floor.codeInUse':
    'This building already has a floor “{value}”.',
  'floor.codeInvalid':
    '“{value}” cannot be used as a floor.',

  /* ── authoring a checklist (W-125/126) ──────────────────────────────────
     Read by somebody about to change the hotel's cleaning standard. Each of these is a
     refusal to let a published version be altered, said in the words of what they were
     trying to do rather than in the words of the rule. */
  'checklist.template.notFound':
    'That checklist could not be found. It may have been removed since this screen loaded.',
  'checklist.draft.notOpen':
    'There is no draft open for this checklist. Press Edit to start one.',
  'checklist.draft.empty':
    'A checklist needs at least one item. An empty list would be given to an attendant '
    + 'with nothing on it.',
  'checklist.draft.tooManyItems':
    'That is {value} items, and the most a checklist can hold is {other}. A list this long '
    + 'is one to split rather than to extend — an attendant scrolls it one-handed, forty '
    + 'times a day.',
  'checklist.draft.changesNothing':
    'This draft is identical to the version in use, so publishing it would create a new '
    + 'version number that differs from the old one in nothing — and somebody will go '
    + 'looking for the difference. Change something, or discard the draft.',
  'checklist.draft.duplicateItem':
    '“{value}” is on this checklist twice. The code is what a report counts by, so it has '
    + 'to name one item.',
  'checklist.draft.itemCodeInvalid':
    '“{value}” cannot be used as an item code. Use capital letters, digits and underscores '
    + '— BATH_TOWELS_REPLACED. It is a key and is never shown to anybody.',
  'checklist.draft.labelMissing':
    'Item “{value}” has no words in one of the two languages. An attendant who reads the '
    + 'other one would be shown a blank line and told to tick it.',
  'checklist.draft.labelTooLong':
    'The words for “{value}” are too long to fit on a handset.',
  'checklist.template.taskTypeUnknown':
    'There is no task type “{value}” in this hotel. A checklist is chosen by matching the '
    + 'task’s type, so this one would never be shown to anybody.',
  'checklist.template.alreadyExists':
    'There is already a checklist for “{value}”. Edit that one — a second would mean two '
    + 'lists competing to be the one an attendant is shown.',

  /* ── the guest form, and the codes behind it ────────────────────────── */
  /* THESE TWO ARE READ BY A GUEST, not by a member of staff, and that changes what they
     have to do. Everything else in this catalogue tells somebody who works here what
     went wrong so they can fix it. These tell a person standing in a corridor holding a
     card what to do NEXT — and the answer is different in each case, which is the whole
     reason they are two codes rather than one refusal. */
  'guest.codeNotRecognised':
    'That code was not recognised. Check it against the card in your room — or ring '
    + 'reception and they will help.',
  /* NOT "too many requests", which reads as a telling-off. Somebody is already coming;
     that is the news. */
  'guest.tooManyOpenRequests':
    'There are already {open} requests open for this room, so someone is on their way. '
    + 'Please ring reception if something is urgent.',

  'qr.alreadyLive':
    'Room {roomNumber} already has a code in use. Revoke that one before issuing another.',
  'qr.notFound':
    'That code no longer exists on this server. Reload the list.',
  'qr.alreadyRevoked':
    'That code has already been revoked.',
  /* Revoking costs a reprint and a walk to the room, so the sentence is the difference
     between "this card was abused" and "we renumbered the floor" when somebody asks in
     six weeks why room 305 has three dead codes. */
  'qr.revoke.reasonRequired':
    'Say why this code is being revoked. The card in the room stops working, so somebody '
    + 'will ask.',
  /* NOT "printing failed". Nothing failed — the server has not been told its own public
     address, and printing anyway would put the wrong host on a card in every bedroom.
     The person who can fix it is whoever set the server up, and the message says so. */
  'qr.baseUrlNotConfigured':
    'This server has not been told its own public address, so the cards cannot be '
    + 'printed — every code on them would point somewhere else. Ask whoever set the '
    + 'server up to set it.',

  /* ── photographs ────────────────────────────────────────────────────── */
  'file.notFound':
    'That photograph is not there any more.',
  /* NOT "not found", and the wording is the point. The row resolved and the bytes did
     not, which is a different event from somebody deleting it — and telling a supervisor
     "not found" is how a storage failure gets recorded as a user action. */
  'file.missingFromStore':
    'This photograph is recorded but the file itself is missing from storage. '
    + 'That is a fault on our side, not something you did — please report it with this reference.',
  'file.typeNotAllowed':
    'That is not a photograph ({contentType}). Take a picture with the camera rather than attaching a file.',
  'file.tooLarge':
    'That picture is too big. Take it again with the camera in the app, which makes it the right size.',
  'file.empty':
    'The camera returned an empty picture. Take it again.',
  'file.tooManyForSubject':
    'There are already {limit} photographs here. Remove one before adding another.',
  'file.subjectNotFound':
    'The thing this photograph belongs to is not there any more. Nothing was saved.',
  'file.alreadyRemoved':
    'That photograph has already been removed.',
  'file.removal.reasonRequired':
    'Say why this photograph is being removed — it is evidence, and the removal is recorded against your name.',

  /* ── the roster ─────────────────────────────────────────────────────── */
  'roster.shiftMismatch':
    'Somebody who is {state} cannot also be on a shift — and somebody on shift needs one. '
    + 'Pick a shift, or change the state.',
  'roster.shift.notFound':
    'That shift is not one this property has. Reload and try again.',
  'roster.stateUnknown':
    'This version of the app sent a roster state the server does not know ({state}). '
    + 'Update the app before changing the roster.',

  /* THE SAME SENTENCE AS THE ROSTER'S, and deliberately so: it is the same event — an
     app older or newer than the server, sending a word the server has no meaning for.
     Naming the value is what turns a support call into a one-line answer. */
  'room.stateUnknown':
    'This version of the app sent a room state the server does not know ({value}). '
    + 'Update the app before moving the room.',

  /* DEFECT #122. This was `validation.failed` — "Something in that was not filled in
     correctly ({field})" — raised with the key `subject`, so BOTH languages drew {field}
     raw. Nothing was filled in at all: a client asked for a kind of attachment this build
     does not have. Its own code, its own sentence, and the same advice its two siblings
     above give. */
  'attachment.subjectUnknown':
    'This version of the app asked for a kind of attachment the server does not know '
    + '({value}). Update the app.',

  /* ── A BODY THE SERVER COULD NOT READ (Sprint 36) ────────────────────────────
     THE ONE REFUSAL NO ENDPOINT MAKES. The request never reached one: ASP.NET could not
     turn what arrived into the shape the endpoint expects.

     THE JSON PATH IS NOT IN THE SENTENCE, DELIBERATELY. The envelope carries one —
     `$.toState`, `$.operations[1].requestedState` — and the server logs it, because it is
     what lets somebody find the field. But the person reading this is an attendant whose
     app is out of date, and their action is the same either way: update it. A path in the
     middle of that sentence is a .NET detail in front of somebody who cannot act on it.
     *The details contract is one-directional precisely so a refusal may carry more than
     its sentence uses.* */
  'request.malformed':
    'This version of the app sent something the server could not read. Update the app, '
    + 'and tell IT if it keeps happening.',
  /* DIFFERENT ADVICE, SO A DIFFERENT CODE. Nothing is wrong with the shape of what was
     sent; there is too much of it, and telling somebody to update the app would send them
     to the wrong place. */
  'request.tooLarge':
    'That was too big for the server to accept. Try again with less at once — a shorter '
    + 'note, or fewer items — and tell IT if it keeps happening.',

  /* ── serviceable areas ──────────────────────────────────────────────── */
  'property.notFound':
    'That property is not one you can see. If you have just been given access, sign in again.',
  'area.notFound':
    'That area is no longer on the schedule. It may have been removed.',
  /* ── W-137 · creating and editing a public area ───────────────────────── */
  'area.codeInvalid':
    'Give the area a short code — up to 40 characters. It is what the reports count by, '
    + 'and it is never translated.',
  'area.codeInUse':
    'Another area already has that code. Codes have to be unique so a report can tell '
    + 'them apart.',
  'area.nameInvalid':
    'Give the area a name — up to 200 characters.',
  'area.kindInvalid':
    'That is not a kind of area this product knows. Choose front of house, corridor, '
    + 'dining, function or back of house.',
  'area.floorNotFound':
    'That floor is not one of this hotel’s. Choose a floor that exists.',
  'area.corridorNeedsFloor':
    'A corridor belongs to a floor. Choose which one — this hotel has more than one '
    + 'third floor, and a passage with no floor cannot be told from another.',
  'area.intervalInvalid':
    'How often it is cleaned has to be at least one minute.',
  'area.windowInvalid':
    'The active hours have to be real times of day. Set both to the same value for an '
    + 'area that is live around the clock.',
  /* ── W-135 · go-live data ──────────────────────────────────────────────────
     None of these should ever reach a person: the screen does not offer a box for the two
     rows the product works out, and it sends the steps it was given. They are here because
     a refusal with no sentence is the worst thing a screen can show, and because an
     integration written against this API later will see them. */
  'goLive.stepUnknown':
    'That is not one of the things this checklist tracks.',
  'goLive.targetIsDerived':
    'This one is worked out rather than typed — every room needs cleaning rules, and '
    + 'every item needs an opening count.',
  'goLive.targetInvalid':
    'Give a number of one or more, or zero to clear it.',
  /* ── W-129 · the hotel's own words ─────────────────────────────────────────
     **THE TIME-ZONE MESSAGE NAMES THE CONSEQUENCE, NOT THE FIELD.** "Invalid time zone"
     tells somebody the box is red; what they need to know is why the product will not
     take a word for it. */
  'config.nameRequired':
    'The hotel needs a name. The Arabic one may be left empty.',
  'config.timeZoneUnknown':
    'This server does not know that time zone, so every date in the hotel would be wrong. '
    + 'Choose one from the list.',
  'config.languageUnknown':
    'This product is drawn in English and Arabic, and a third would leave the screens in '
    + 'neither.',
  'config.roleNotFound':
    'That role is no longer here. Reload the page to see the current list.',
  'config.roleNameRequired':
    'A role needs a name. The Arabic one may be left empty.',

  /* **W-129, SECOND CARD — THE HOTEL'S OWN LIST OF REQUESTS.** Sprint 56.

     Every one of these names the CONSEQUENCE rather than the field, because this is the
     screen where the consequences are least obvious: nothing on it looks dangerous, and
     three of these rules exist because the failure they prevent is silent everywhere else
     in the product. */
  'requestCategory.notFound':
    'That kind of request is no longer here. Reload the page to see the current list.',
  'requestCategory.nameRequired':
    'This needs a name. The Arabic one may be left empty.',
  'requestCategory.codeShape':
    'A short code is capital letters, digits and underscores, starting with a letter — '
    + 'EXTRA_TOWELS, PRAYER_MAT. It is what every report counts by, and it can never be '
    + 'changed afterwards.',
  'requestCategory.codeTaken':
    'This hotel already uses that short code. If you cannot see it in the list, it is '
    + 'there but switched off — switch it back on rather than adding it again.',
  'requestCategory.promiseOutOfRange':
    'A promise has to be between {min} and {max} minutes. Nothing can be delivered in no '
    + 'time at all, and past a day it is a maintenance job rather than a guest request.',
  'requestCategory.stockItemUnknown':
    'That stock item is not one this hotel is holding, so delivering this would promise a '
    + 'movement that never happens.',
  'requestCategory.lastActive':
    'This is the only kind of request left switched on. Turning it off would leave the '
    + 'front desk and every QR code in the building with nothing to choose.',
  'requestCategory.inUse':
    'Guests have asked for this {requests} times, so it cannot be removed — every one of '
    + 'those requests would point at something with no name. Switch it off instead and it '
    + 'stops being offered.',
  'requestCategory.alreadyInState':
    'Somebody else has already changed this. Reload the page to see where it stands.',
  'area.closed':
    'This area is closed, so a clean cannot be recorded against it. '
    + 'If it has reopened, a supervisor needs to reopen it here first.',

  /* Closing a public area by hand, Sprint 57. The same three refusals as a room's, worded
     for a place rather than a bedroom — a supervisor told "the room is out of order" while
     looking at a washroom has been told the product is confused. */
  'area.service.reasonRequired':
    'Say why in a sentence. An area that has quietly stopped appearing on anybody\u2019s list '
    + 'is the hardest thing in this product to explain later — and reopening one needs it '
    + 'more, because it goes straight back onto the schedule.',
  'area.service.unchanged':
    'This area is already where you are putting it. Somebody else may have got there first '
    + '\u2014 reload to see where it stands.',
  'area.isOutOfOrder':
    'A fault has this area out of order, so it is not yours to open or close here. It comes '
    + 'back when the fault is resolved, which keeps the fault on somebody\u2019s list instead '
    + 'of hiding it.',

  /* W-128 — every word this product invented, Sprint 59. Both of these mean the screen and
     the server disagree about what is there, which is almost always a stale page. */
  'translation.kindUnknown':
    'This server does not translate that kind of thing. Reload the page.',
  'translation.notFound':
    'That row is no longer here — it may have been removed while this page was open. '
    + 'Reload to see the current list.',

  /* ── linen and consumables ─────────────────────────────────────────────── */
  'stock.item.notFound':
    'That item is no longer in the list. Reload and try again.',
  /* CQ-136. **ZERO IS NOT REFUSED** — it removes the standard, because a standard of zero
     is that row not existing. This is about a figure nobody could carry. */
  'linen.quantityInvalid':
    '{value} is not an amount a room takes. Use a number up to 99, or clear the box to '
    + 'carry none of it at all.',
  'stock.location.notFound':
    'That store or pantry is no longer set up. Ask an administrator.',
  'stock.place.inUse':
    'This place is in {movements} recorded movements, so it cannot be removed — '
    + 'every one of them points at it. Switch it off instead.',
  'stock.place.lastOfKind':
    'This is the last one of its kind. Switching it off would leave nothing able to '
    + 'send or receive stock at all.',
  'stock.item.inUse':
    'This item is in {movements} recorded movements, so it cannot be removed — '
    + 'every one of them points at it. Switch it off instead.',
  'stock.item.kindSettled':
    'Whether this comes back was settled by its first movement, and there are now '
    + '{movements}. Changing it would not correct them — it would change what every one '
    + 'of them meant when it was recorded.',
  'stock.item.inStandard':
    'This item is named by {standards} linen standards. Take it out of those first, '
    + 'so that nobody is told to carry something the store can no longer issue.',
  'shift.notFound':
    'That shift is no longer set up. Reload the page.',
  'shift.lastActive':
    'This is the last shift still switched on. With none, nobody can be rostered on '
    + 'shift at all — the morning sweep would place no work and the board would be empty.',
  'stock.wouldGoNegative':
    'There are only {available} there — you asked for {requested}. '
    + 'If the shelf says otherwise, record a stock correction rather than forcing it.',
  'stock.reasonRequired':
    'Say why before writing this off — it changes what the hotel is recorded as owning.',
  'stock.movement.invalid':
    'That movement does not make sense for a {kind} ({field}).',
  'stock.movement.kindUnknown':
    'This version of the app sent a movement type the server does not know ({kind}). '
    + 'Update the app before recording this.',
  'auth.sessionUnknown':
    'This device is not signed in any more. Sign in again — anything waiting to send '
    + 'will go once you do.',
  /* ENDED IS NOT EXPIRED, AND THE ADVICE IS DIFFERENT. Telling somebody to sign in
     again, when signing in again is exactly what they must not do until they have
     spoken to a supervisor, wastes their morning and teaches them the message is noise. */
  'auth.sessionEnded':
    'This device was signed out by an administrator. Speak to your supervisor before '
    + 'signing in again.',

  'user.notFound':
    'That person is not in the system.',
  'user.employeeIdTaken':
    'Employee number {employeeId} already belongs to somebody. An employee number is '
    + 'never reused — reusing one merges two people’s work history for good.',
  'user.employeeIdRequired':
    'An employee number is needed. It is how somebody signs in.',
  'user.nameRequired':
    'A name is needed.',
  'user.roleNotFound':
    'That role no longer exists.',
  'user.propertyNotFound':
    'That property no longer exists.',
  'user.reasonRequired':
    'Say why. Disabling somebody ends every session they have open, immediately.',
  'user.wouldLockYouOut':
    'This would end your own access, and there is no way back in from this screen. '
    + 'Ask another administrator to do it.',
  'user.lastAdministrator':
    'This is the only person who holds {permissionCode}, and it is one of the two rights '
    + 'the hotel cannot be without. Give it to somebody else first, or nobody will be '
    + 'able to fix an access problem.',
  'session.notFound':
    'That session no longer exists.',

  'permission.unknownCode':
    'This version of the app sent a capability the server does not know ({permissionCode}). '
    + 'Update the app before changing permissions.',
  'permission.grantUnknown':
    'This version of the app sent a setting the server does not know ({grant}). '
    + 'Update the app before changing permissions.',
  'activity.filterUnknown':
    'This version of the app asked the activity log for a filter the server does not '
    + 'know ({filter}). Nothing was hidden — reload the page to see everything.',
  'permission.reasonMeaningless':
    'Nothing about {permissionCode} asks anybody for a reason, so “with a reason recorded” '
    + 'would behave exactly like “allowed” while looking as though it did not.',
  /* THE SENTENCE NAMES A WAY FORWARD, not only a refusal. Somebody who has just been told
     no needs to know which roles hold it now, or the only next move is to guess. */
  'permission.wouldLockEverybodyOut':
    'This would leave nobody able to administer the system — {permissionCode} would be held '
    + 'by no role anybody has. It is currently held by: {heldBy}. Give it to somebody else '
    + 'in the same save.',

  'stock.count.notFound':
    'That stock count no longer exists.',
  /* THE REFERENCE, NOT THE ID. Whoever reads this is standing at a shelf, and the useful
     next act is to go and ask the person holding CNT-0034 whether they have finished. */
  'stock.count.locationHeld':
    'Count {countReference} is under way here, so stock cannot be moved in or out until '
    + 'it is approved. This is what stops the count drifting while somebody is walking '
    + 'the shelves.',
  'stock.count.alreadyOpenHere':
    'Count {countReference} is already open at this location. Carry on with that one '
    + 'rather than starting a second.',
  'stock.count.wrongState':
    'This count is {state}, so that cannot be done to it now.',
  'stock.count.nothingCounted':
    'Nothing has been counted yet. Put at least one figure in before handing it over.',
  'stock.count.approverCounted':
    'You counted this one, so somebody else has to approve it. That is deliberate: '
    + 'counting a shortfall and authorising the write-off are two decisions.',
  'stock.count.reasonRequired':
    'Say why. This throws away counting somebody did on their feet.',
  'stock.count.itemNotOnSheet':
    'That item is not on this count sheet. The sheet was probably taken before the item '
    + 'existed — start a new count.',
  'stock.count.adjustmentUnknown':
    'This version of the app sent a decision the server does not know ({adjustment}). '
    + 'Update the app before approving this count.',
  'stock.count.negativeQuantity':
    'A shelf cannot hold less than none. If there are none, count it as zero.',

  /* ── CQ-42 · an adjustment waits for a second person (Sprint 82) ──────────
     **EVERY ONE OF THESE SAYS WHAT TO DO NEXT**, because the act the person wanted is
     still available — it is simply not theirs alone. A refusal that only says "no"
     teaches somebody that the product is arbitrary. */
  'stock.adjustment.needsApproval':
    'A correction to a shelf figure has to be approved by somebody else before it counts. '
    + 'Send it for approval instead and whoever else holds stock rights will see it.',
  'stock.adjustment.notFound':
    'That correction is no longer here. Somebody may have decided it while you were '
    + 'looking at it.',
  'stock.adjustment.alreadyDecided':
    'This one has already been decided ({state}). Nothing has changed a second time.',
  'stock.adjustment.approverRaised':
    'You raised this one, so somebody else has to decide it. That is deliberate: saying '
    + 'the shelf is wrong and authorising the correction are two decisions.',
  'stock.adjustment.noteRequired':
    'Say why you are turning it down. Somebody counted something and is owed a sentence.',
  'stock.adjustment.zeroQuantity':
    'A correction of nothing is not a correction. Put in what the figure should move by.',

  /* ── CQ-26 · the five things that raise work (Sprint 83) ──────────────────
     **THE REFUSAL NAMES THE SWITCH.** The person refused has done nothing wrong and the
     thing that refused them is one screen away; a refusal that only says "no" teaches
     somebody that the product is arbitrary. */
  'work.trigger.switchedOff':
    'This hotel has switched that off, so work of this kind is not raised any more '
    + '({trigger}). An administrator can switch it back on under Settings.',
  'work.trigger.unknown':
    'This version of the app sent a trigger the server does not know ({trigger}). Update '
    + 'the app.',
  'work.trigger.intervalRequired':
    'Say how many days first. A room refresh needs to know how long a made-up room may '
    + 'stand before somebody goes over it again — and only this hotel knows that.',
  'work.trigger.intervalOutOfRange':
    'That is not a rhythm ({days} days). Anything from one day to a year.',

  'validation.failed':
    'Something in that was not filled in correctly ({field}).',

  'permission.denied':
    'Your role does not allow this. Your supervisor can request access for you.',
  'permission.reasonRequired':
    'You may do this, but it has to be recorded with a reason.',

  'sync.lateTransitionConflict':
    'Room {roomNumber} changed while this device was offline, so the queued update was not applied. Check the room and record it again.',
  'sync.idempotencyKeyReusedWithDifferentPayload':
    'This device sent two different updates under the same reference. Nothing was changed. Please report this.',
}

const ar: Catalogue = {
  'room.notFound':
    'تعذّر العثور على الغرفة {roomNumber}. ربما أُزيلت بعد فتح هذه الشاشة.',
  'room.doorCode.notRecognised':
    'هذا ليس رمز باب من هذا الفندق. ابحث عن الغرفة في قائمتك — المسح اختصار فقط.',
  'room.doorCode.replaced':
    'تم استبدال البطاقة على هذا الباب، ولم يعد الرمز القديم يعمل. '
    + 'استخدم قائمتك، وأبلغ المشرف أن البطاقة القديمة ما زالت معلّقة.',
  'room.transition.notPermitted':
    'لا يمكن نقل الغرفة {roomNumber} من {fromState} إلى {toState}.',
  'room.transition.reasonRequired':
    'يحتاج هذا التغيير إلى سبب قبل تسجيله.',
  'room.changedConcurrently':
    'قام شخص آخر بتغيير الغرفة {roomNumber} أثناء فتح هذه الشاشة. حدّث الصفحة لعرض الحالة الحالية.',

  'task.notFound':
    'تعذّر العثور على المهمة. ربما اكتملت أو أُلغيت.',
  'task.state.notPermitted':
    'لا يمكن نقل المهمة إلى {toState} من وضعها الحالي.',
  'task.notAssigned':
    'لم تُسنَد هذه المهمة إلى أحد بعد.',

  'task.needsRoomXorArea':
    'اختر إمّا غرفة وإمّا منطقة عامة لهذا العمل — واحدة منهما لا كلتيهما.',
  'task.typeUnknown':
    'لا يوجد في هذا الفندق نوع عمل باسم «{value}». حدّث الصفحة ثم اختر من جديد.',
  'task.roomNotInService':
    'الغرفة {roomNumber} خارج الخدمة، فلا يمكن إرسال أحد لتنظيفها. أعِدها إلى الخدمة من '
    + 'لوحة الغرف أولًا.',
  'task.cancel.reasonRequired':
    'اذكر سبب إلغاء هذا العمل. فمن أُسندت إليه الغرفة سيريد معرفته، وكذلك من يقرأ السجل '
    + 'بعد ستة أسابيع.',
  'work.schedule.minuteOutOfRange':
    'هذا ليس وقتًا في اليوم. اختر وقتًا بين ٠٠:٠٠ و٢٣:٥٩، أو أوقف الجدولة للعودة إلى '
    + 'إنشاء أعمال اليوم يدويًا.',
  'task.inspection.selfForbidden':
    'لا يمكنك تفتيش عمل قمت به بنفسك. يجب أن يقوم مشرف آخر بتفتيش هذه الغرفة.',
  'task.inspection.reasonRequired':
    'يحتاج رفض التفتيش إلى سبب، ليعرف الموظف ما الذي يجب تصحيحه.',
  'task.assigneeNotOnShift':
    'هذا الموظف غير موجود على العمل اليوم، لذا لا يمكن إسناد العمل إليه. راجع جدول المناوبات أو اختر موظفًا على المناوبة.',
  'task.teamNotOnShift':
    'كلا الموظفَين غير موجود اليوم، لذا لا يمكن إسناد هذه الغرفة إلى هذا الثنائي. اختر من هو على المناوبة، أو سجّل بديلًا أولًا.',
  'task.room.noCleaningRules':
    'لم تُحدَّد قواعد التنظيف للغرفة {roomNumber}، لذا لا يمكن إسنادها إلى أحد بعد. '
    + 'حدِّد قوائم التحقق ودقائق التنظيف من شاشة الغرف والمناطق.',
  'task.linen.recordingClosed':
    'تم تسليم هذه الغرفة، لذا صارت أرقام المفروشات من صلاحية مشرفك. أبلغه بما اختلف.',
  'task.linen.reasonRequired':
    'تغيير رقم مفروشات سجّله شخص آخر يحتاج إلى سبب.',
  'task.linen.itemNotOnStandard':
    'هذا ليس مما تحتاجه هذه الغرفة، فلا مكان لتسجيله.',
  'trolley.notAssigned':
    'لا توجد لديك عربة اليوم. اسأل مشرفك أي عربة تخصك.',
  'trolley.itemNotCarried':
    'هذا ليس مما تُحصى به العربة، فلا مكان لتسجيله.',
  'trolley.quantityOutOfRange':
    'هذا أكثر مما تتسع له العربة. تحقّق من الرقم وحاول مرة أخرى.',
  'task.checklist.notFound':
    'لا توجد قائمة تحقق معدّة لهذا النوع من المهام.',
  'task.checklist.itemNotOnThisList':
    'هذا البند ليس ضمن هذه النسخة من قائمة التحقق. أعد تحميل الغرفة للحصول على القائمة الحالية.',
  'task.checklist.exceptionNeedsNote':
    'اذكر ما الذي منعك قبل تعليم هذا البند كاستثناء.',
  'task.checklist.incomplete':
    'ما زال {outstandingCount} من بنود قائمة التحقق بحاجة إلى إجابة قبل تسليم الغرفة.',
  'task.checklist.photographRequired':
    'مطلوب صورة لعدد {count} من البنود التي أنجزتها قبل تسليم الغرفة. افتح البند واستخدم الكاميرا.',

  /* ── sign-in ─────────────────────────────────────────────────────────── */
  'auth.notSignedIn':
    'لم تسجّل الدخول. سجّل الدخول مرة أخرى للمتابعة.',
  'auth.userNotFound':
    'هذا الحساب لم يعد نشطًا هنا. يمكن لمدير النظام إعادة تفعيله.',
  'auth.assertionRejected':
    'لم يُقبل تسجيل الدخول. حاول مرة أخرى، وأبلغ قسم تقنية المعلومات إذا تكرر ذلك.',
  'auth.noAccount':
    'تم تسجيل دخولك، لكن رقم الموظف {employeeId} ليس له أي دور هنا بعد. '
    + 'يجب على مدير النظام إسناد دور قبل أن تتمكن من البدء.',

  /* ── الأعطال وطلبات النزلاء ───────────────────────────────────────────── */
  'service.valueUnknown':
    'أرسل هذا الإصدار من التطبيق قيمة لا يعرفها الخادم ({value}). حدّث التطبيق.',
  'issue.notFound':
    'هذا البلاغ لم يعد موجودًا. ربما أغلقه شخص آخر.',
  'issue.state.notPermitted':
    'لا يمكن نقل بلاغ حالته {from} إلى {to}.',
  'issue.reasonRequired':
    'اذكر السبب قبل إغلاق هذا البلاغ — سيراه من قام بالإبلاغ.',
  'request.notFound':
    'هذا الطلب لم يعد موجودًا. ربما أغلقه شخص آخر.',
  'request.state.notPermitted':
    'لا يمكن نقل طلب حالته {from} إلى {to}.',
  'request.reasonRequired':
    'اذكر سبب رفض الطلب — سيُبلَّغ النزيل بالرفض.',

  'auth.stagingKeyRequired':
    'هذا خادم الاختبار، ويحتاج مفتاح الدخول الذي أعطاك إياه مدير النظام. '
    + 'ليست كلمة مرورك — جميع المختبرين يستخدمون المفتاح نفسه.',
  'auth.sessionExpired':
    'انتهت جلستك. سجّل الدخول من جديد للمتابعة — وما كان بانتظار الإرسال ما زال محفوظًا.',

  /* ── التقارير ───────────────────────────────────────────────────────── */
  'report.notFound':
    'هذا التقرير غير موجود على هذا الخادم. أعد تحميل القائمة.',
  'report.period.invalid':
    'نهاية الفترة تسبق بدايتها ({from} إلى {to}).',
  'report.period.tooLong':
    'يمكن أن يغطّي التقرير {maxDays} يومًا كحدّ أقصى. اختر فترة أقصر.',
  /* WITHOUT THE ACRONYM. "PDF" is Latin, and the catalogue guard that refuses Latin
     letters in an Arabic message is right to refuse it — it exists to catch the
     half-translated string, and a rule with a convenient exception stops catching
     anything. "ملف للطباعة" says the same thing and reads better. */
  'report.pdfUnavailable':
    'لا يستطيع هذا الخادم إنشاء ملف للطباعة — لا يوجد متصفّح مثبّت لذلك. '
    + 'ما زال بإمكانك قراءة التقرير على الشاشة. راجع من أعدّ الخادم.',

  /* ── تحميل غرف الفندق ───────────────────────────────────────────────── */
  'load.noRows':
    'لا توجد غرف في هذا الملف.',
  'load.tooManyRows':
    'يحتوي الملف على {value} صفًا، والحد الأقصى للتحميل دفعة واحدة هو {other}. '
    + 'ملف بهذا الحجم عادةً ما يكون تصديرًا لشيء آخر غير الغرف.',
  'load.missingColumn':
    'لا يحتوي الملف على عمود «{value}». يجب أن يحمل الصف الأول أسماء الأعمدة — '
    + 'رقم الغرفة والطابق والنوع هي الثلاثة التي لا يمكن قراءة الملف بدونها.',
  'load.notASpreadsheet':
    'تعذّر فتح هذا الملف كجدول بيانات. احفظه بصيغة اكسل ثم أعد المحاولة.',
  'load.textEncodingWrong':
    'لم يُحفظ هذا الملف بطريقة تحافظ على النص العربي. اختر عند الحفظ صيغة النص المفصول '
    + 'بفواصل بترميز يونيكود، أو أرسل ملف اكسل بدلًا منه.',
  'load.fileTooLarge':
    'هذا الملف أكبر من أن يكون قائمة غرف. أرسل ورقة الغرف وحدها.',
  'load.roomNumberMissing':
    'لا يوجد رقم غرفة في هذا السطر.',
  'load.buildingMissing':
    'لا يوجد مبنى في هذا السطر. الملف يذكر المبنى في بقية صفوفه، وترك هذا الصف فارغًا '
    + 'يعني وضع الغرفة في مبنى مُخمَّن.',
  'load.buildingColumnRequired':
    'في هذا الفندق {value} مبانٍ ({other})، لذا يحتاج الملف إلى عمود «المبنى» — '
    + 'بدونه لا سبيل لمعرفة الطابق في أي مبنى.',
  'load.floorMissing':
    'لا يوجد طابق في هذا السطر.',
  'load.typeMissing':
    'لا يوجد نوع غرفة في هذا السطر.',
  'load.valueTooLong':
    '«{value}» أطول مما يمكن تخزينه.',
  'load.cleanMinutesNotANumber':
    '«{value}» ليس عددًا من الدقائق.',
  'load.cleanMinutesOutOfRange':
    '{value} دقيقة خارج المدة الممكنة للتنظيف. استخدم رقمًا بين ٥ و٢٤٠.',
  'load.capacityNotANumber':
    '«{value}» ليس عددًا من الأشخاص.',
  'load.capacityOutOfRange':
    'لا يمكن أن تتّسع الغرفة لـ {value} أشخاص. استخدم رقمًا بين ١ و١٢ — غرف هذا الفندق '
    + 'تتّسع لشخصين أو لأربعة.',
  'load.capacityConflict':
    'نوع الغرفة {value} يتّسع هنا لعدد يختلف عمّا ورد في السطر {other}. لكل رمز سعة '
    + 'واحدة — وإن كانتا غرفتين مختلفتين فلكلٍّ رمزها.',
  'load.duplicateRoom':
    'الغرفة {value} مذكورة أيضًا في السطر {other} من هذا الملف. احذف أحدهما.',
  'load.typeNameConflict':
    'نوع الغرفة {value} مسمّى هنا بغير ما ورد في السطر {other}. لكل رمز اسم واحد.',
  'load.fileHasProblems':
    'ما زال في الملف {problems} بحاجة إلى تصحيح، فلم يُحمَّل شيء. صحّحها ثم ارفعه من '
    + 'جديد — التحميل كامل أو لا شيء، حتى لا يبقى الفندق محمّلًا نصف تحميل.',

  /* ── تعديل غرفة واحدة ───────────────────────────────────────────────── */
  'room.numberInUse':
    'توجد في هذا الفندق غرفة بالرقم {roomNumber} أصلًا.',
  'room.numberHeldByRemovedRoom':
    'الغرفة {roomNumber} موجودة لكنها خارج الفندق، وما زالت تحتفظ بهذا الرقم. '
    + 'أعِدها إلى الفندق بدل إعطاء رقمها لغرفة ثانية.',
  'room.numberInvalid':
    'لا يصلح «{value}» رقمًا لغرفة.',
  'room.floorNotFound':
    'هذا الطابق ليس من طوابق هذا الفندق. حدّث الصفحة ثم اختر من جديد.',
  'room.buildingNotFound':
    'هذا المبنى ليس من مباني هذا الفندق. حدّث الصفحة ثم اختر من جديد.',

  'team.notFound':
    'هذا الفريق لم يعد موجودًا. حدّث الصفحة — قد يكون أحدهم قد فكّ الثنائي.',
  'team.samePersonTwice':
    'الفريق رجلان مختلفان. اختر شخصًا آخر للمكان الثاني.',
  'team.memberAlreadyPaired':
    '{name} على فريق بالفعل. أخرِجه من ذلك الفريق أولًا، أو اختر شخصًا غير مرتبط بفريق.',
  'team.standInAlreadyOnTeam':
    '{name} على هذا الفريق أصلًا، فلا يمكنه أن ينوب عن زميله. اختر شخصًا من طابق آخر.',
  'team.notOnThisTeam':
    '{name} ليس على هذا الفريق. ربما تغيّر الثنائي والشاشة مفتوحة — حدّثها ثم أعد المحاولة.',
  'room.typeNotFound':
    'هذا النوع ليس من أنواع غرف هذا الفندق. حدّث الصفحة ثم اختر من جديد.',
  'room.typeNameRequired':
    'أعطِ {value} اسمًا. الرمز هو ما تُحصى به التقارير، والاسم هو ما يقرؤه الموظفون على '
    + 'كل بطاقة.',
  'room.typeCapacityInvalid':
    'لا يمكن أن تتّسع الغرفة لـ {value} أشخاص. استخدم رقمًا بين ١ و١٢ — غرف هذا الفندق '
    + 'تتّسع لشخصين أو لأربعة.',
  'room.removal.reasonRequired':
    'اذكر سبب إخراج هذه الغرفة من الفندق. سيسأل أحدهم عنه بعد ستة أسابيع.',
  'room.restore.reasonRequired':
    'اذكر سبب إعادة هذه الغرفة. فهي تعود للبيع فور إعادتها.',
  'room.alreadyRemoved':
    'الغرفة {roomNumber} خارج الفندق أصلًا.',
  'room.service.reasonRequired':
    'اذكر السبب. غرفة مسحوبة من البيع بلا سبب مدوَّن لا يجرؤ أحد على إعادتها.',
  'room.service.unchanged':
    'الغرفة {roomNumber} على هذه الحال أصلًا — ربما قام أحدهم بذلك للتو.',
  'room.isOutOfOrder':
    'الغرفة {roomNumber} خارج الخدمة بسبب عطل مُبلَّغ عنه. عالِج العطل لإعادتها للبيع.',
  'room.notRemoved':
    'الغرفة {roomNumber} داخل الفندق أصلًا.',
  'room.hasWorkInHand':
    'أُرسل أحدهم إلى الغرفة {roomNumber}، وعليها عمل لم ينتهِ بعد. ألغِ العمل من لوحة '
    + 'التوزيع أولًا، ثم أخرِج الغرفة.',
  /* `وحدة عمل`, and the first draft of this file got it BACKWARDS — see defect #115.
     Sprint 32 changed this sentence FROM `وحدة` TO `نقطة`, with a comment claiming that
     `نقطة` was what every other string used. It was not: the assignment board, the screen
     this number exists for, had said `وحدة عمل` since Sprint 6. One concept, one word. */
  'property.minutesPerCredit.outOfRange':
    'لا يمكن أن تكون {minutes} دقيقة وحدةَ عملٍ واحدة. استخدم رقمًا بين ٥ و١٢٠ — فما دون '
    + 'ذلك تصبح غرفة واحدة بقيمة دوام كامل، وما فوقه لا تبلغ أي غرفة وحدةً واحدة.',
  'building.codeInUse':
    'يوجد في هذا الفندق مبنى «{value}» أصلًا.',
  'building.codeInvalid':
    'لا يصلح «{value}» مبنًى.',
  'floor.codeInUse':
    'يوجد في هذا المبنى طابق «{value}» أصلًا.',
  'floor.codeInvalid':
    'لا يصلح «{value}» طابقًا.',

  /* ── تحرير قوائم التنظيف ─────────────────────────────────────────────── */
  'checklist.template.notFound':
    'تعذّر العثور على هذه القائمة. ربما أُزيلت بعد فتح الشاشة.',
  'checklist.draft.notOpen':
    'لا توجد مسوّدة مفتوحة لهذه القائمة. اضغط «تعديل» لبدء واحدة.',
  'checklist.draft.empty':
    'لا بدّ للقائمة من بند واحد على الأقل. القائمة الفارغة تصل إلى العامل وليس فيها شيء.',
  'checklist.draft.tooManyItems':
    'عدد البنود {value}، والحدّ الأقصى {other}. قائمة بهذا الطول تُقسَّم ولا تُمدَّد — '
    + 'فالعامل يتصفّحها بيد واحدة أربعين مرة في اليوم.',
  'checklist.draft.changesNothing':
    'هذه المسوّدة مطابقة للنسخة المعمول بها، ونشرها ينشئ رقم نسخة لا يختلف عن سابقه في '
    + 'شيء — وسيبحث أحدهم عن الفرق. غيّر شيئًا أو احذف المسوّدة.',
  'checklist.draft.duplicateItem':
    'البند «{value}» مكرّر في هذه القائمة. الرمز هو ما تُحصى به التقارير، فلا بدّ أن يدلّ '
    + 'على بند واحد.',
  'checklist.draft.itemCodeInvalid':
    'لا يصلح «{value}» رمزًا للبند. استعمل الحروف اللاتينية الكبيرة والأرقام والشرطة '
    + 'السفلية. الرمز مفتاح داخلي ولا يظهر لأحد.',
  'checklist.draft.labelMissing':
    'البند «{value}» بلا نصّ في إحدى اللغتين. من يقرأ اللغة الأخرى سيرى سطرًا فارغًا '
    + 'ويُطلب منه تأشيره.',
  'checklist.draft.labelTooLong':
    'نصّ البند «{value}» أطول من أن يظهر على الجهاز.',
  'checklist.template.taskTypeUnknown':
    'لا يوجد نوع مهمة «{value}» في هذا الفندق. تُختار القائمة بمطابقة نوع المهمة، فلن '
    + 'تظهر هذه لأحد أبدًا.',
  'checklist.template.alreadyExists':
    'توجد قائمة لـ«{value}» أصلًا. عدّل تلك القائمة — فوجود قائمتين يعني تنافسهما على من '
    + 'تظهر للعامل.',

  /* ── نموذج النزيل والرموز ───────────────────────────────────────────── */
  'guest.codeNotRecognised':
    'لم يتم التعرّف على هذا الرمز. تأكّد منه على البطاقة الموجودة في غرفتك، '
    + 'أو اتصل بالاستقبال وسيساعدونك.',
  'guest.tooManyOpenRequests':
    'لدى هذه الغرفة {open} طلبات قيد التنفيذ، وأحدهم في طريقه إليك. '
    + 'اتصل بالاستقبال إن كان الأمر عاجلًا.',

  'qr.alreadyLive':
    'الغرفة {roomNumber} لديها رمز قيد الاستخدام. ألغِ ذلك الرمز قبل إصدار رمز جديد.',
  'qr.notFound':
    'لم يعد هذا الرمز موجودًا على هذا الخادم. أعد تحميل القائمة.',
  'qr.alreadyRevoked':
    'سبق إلغاء هذا الرمز.',
  'qr.revoke.reasonRequired':
    'اذكر سبب إلغاء هذا الرمز. ستتوقّف البطاقة الموجودة في الغرفة عن العمل، وسيسأل أحدهم عن السبب.',
  'qr.baseUrlNotConfigured':
    'لم يُحدَّد لهذا الخادم عنوانه العام، لذا لا يمكن طباعة البطاقات — إذ سيشير كل رمز '
    + 'عليها إلى موضع آخر. راجع من أعدّ الخادم لضبطه.',

  /* ── الصور ──────────────────────────────────────────────────────────── */
  'file.notFound':
    'لم تعد هذه الصورة موجودة.',
  'file.missingFromStore':
    'الصورة مسجّلة لكن الملف نفسه مفقود من وحدة التخزين. '
    + 'هذا خلل لدينا وليس بسببك — يرجى الإبلاغ عنه مع هذا المرجع.',
  'file.typeNotAllowed':
    'هذا ليس صورة ({contentType}). التقط صورة بالكاميرا بدلًا من إرفاق ملف.',
  'file.tooLarge':
    'حجم الصورة كبير جدًا. أعد التقاطها بكاميرا التطبيق فهي تضبط الحجم المناسب.',
  'file.empty':
    'أعادت الكاميرا صورة فارغة. أعد الالتقاط.',
  'file.tooManyForSubject':
    'يوجد بالفعل {limit} من الصور هنا. احذف واحدة قبل إضافة أخرى.',
  'file.subjectNotFound':
    'لم يعد العنصر الذي تخصّه هذه الصورة موجودًا. لم يُحفظ شيء.',
  'file.alreadyRemoved':
    'سبق حذف هذه الصورة.',
  'file.removal.reasonRequired':
    'اذكر سبب حذف هذه الصورة — فهي دليل، ويُسجَّل الحذف باسمك.',

  /* ── جدول الدوام ─────────────────────────────────────────────────────── */
  'roster.shiftMismatch':
    'من حالته {state} لا يمكن أن يكون على وردية — ومن هو على وردية لا بد له منها. '
    + 'اختر وردية، أو غيّر الحالة.',
  'roster.shift.notFound':
    'هذه الوردية ليست من ورديات هذا الفندق. أعد التحميل وحاول مرة أخرى.',
  'roster.stateUnknown':
    'أرسل هذا الإصدار من التطبيق حالة دوام لا يعرفها الخادم ({state}). '
    + 'حدّث التطبيق قبل تعديل جدول الدوام.',

  'room.stateUnknown':
    'أرسل هذا الإصدار من التطبيق حالة غرفة لا يعرفها الخادم ({value}). '
    + 'حدّث التطبيق قبل نقل الغرفة.',

  'attachment.subjectUnknown':
    'طلب هذا الإصدار من التطبيق نوع مرفق لا يعرفه الخادم ({value}). حدّث التطبيق.',

  'request.malformed':
    'أرسل هذا الإصدار من التطبيق شيئًا تعذّر على الخادم قراءته. حدّث التطبيق، وأبلغ قسم '
    + 'تقنية المعلومات إذا تكرّر ذلك.',
  'request.tooLarge':
    'الحجم أكبر مما يقبله الخادم. أعد المحاولة بكمية أقل — ملاحظة أقصر أو عدد أقل من '
    + 'العناصر — وأبلغ قسم تقنية المعلومات إذا تكرّر ذلك.',

  /* ── المناطق العامة ──────────────────────────────────────────────────── */
  'property.notFound':
    'هذا الفندق ليس ضمن صلاحياتك. إذا مُنحت الصلاحية للتو فسجّل الدخول من جديد.',
  'area.notFound':
    'هذه المنطقة لم تعد ضمن الجدول. ربما تم حذفها.',
  /* ── W-137 · إنشاء منطقة عامة وتعديلها ─────────────────────────────── */
  'area.codeInvalid':
    'أعطِ المنطقة رمزًا قصيرًا لا يتجاوز ٤٠ حرفًا. الرمز هو ما تُحصى به التقارير ولا يُترجم.',
  'area.codeInUse':
    'يوجد منطقة أخرى بهذا الرمز. يجب أن تكون الرموز فريدة ليستطيع التقرير التمييز بينها.',
  'area.nameInvalid':
    'أعطِ المنطقة اسمًا لا يتجاوز ٢٠٠ حرف.',
  'area.kindInvalid':
    'هذا ليس نوع منطقة يعرفه النظام. اختر: واجهة أمامية، أو ممر، أو مطعم، أو قاعة، أو '
    + 'منطقة خدمية.',
  'area.floorNotFound':
    'هذا الطابق ليس من طوابق هذا الفندق. اختر طابقًا موجودًا.',
  'area.corridorNeedsFloor':
    'الممر يتبع طابقًا. اختر الطابق — في هذا الفندق أكثر من طابق ثالث، وممرٌّ بلا طابق لا '
    + 'يمكن تمييزه عن غيره.',
  'area.intervalInvalid':
    'مدة التنظيف يجب ألا تقل عن دقيقة واحدة.',
  'area.windowInvalid':
    'ساعات العمل يجب أن تكون أوقاتًا حقيقية في اليوم. اجعل القيمتين متساويتين للمنطقة '
    + 'التي تعمل على مدار الساعة.',
  'goLive.stepUnknown':
    'هذا ليس من بين ما تتابعه هذه القائمة.',
  'goLive.targetIsDerived':
    'هذا البند يُحتسب تلقائيًا ولا يُكتب — كل غرفة تحتاج قواعد تنظيف، وكل صنف يحتاج جردًا '
    + 'افتتاحيًا.',
  'goLive.targetInvalid':
    'اكتب رقمًا واحدًا فأكثر، أو صفرًا لإلغائه.',
  'config.nameRequired':
    'يحتاج الفندق إلى اسم. أما الاسم العربي فيمكن تركه فارغًا.',
  'config.timeZoneUnknown':
    'هذا الخادم لا يعرف هذه المنطقة الزمنية، وستصبح كل التواريخ في الفندق خاطئة. '
    + 'اختر واحدة من القائمة.',
  'config.languageUnknown':
    'هذا النظام معروض بالإنجليزية والعربية، وأي لغة ثالثة ستترك الشاشات بلا أيٍّ منهما.',
  'config.roleNotFound':
    'لم يعد هذا الدور موجودًا. أعِد تحميل الصفحة لرؤية القائمة الحالية.',
  'config.roleNameRequired':
    'يحتاج الدور إلى اسم. أما الاسم العربي فيمكن تركه فارغًا.',
  'requestCategory.notFound':
    'لم يعد هذا النوع من الطلبات موجودًا. أعِد تحميل الصفحة لرؤية القائمة الحالية.',
  'requestCategory.nameRequired':
    'يحتاج هذا إلى اسم. أما الاسم العربي فيمكن تركه فارغًا.',
  'requestCategory.codeShape':
    'الرمز المختصر يتكوّن من حروف إنجليزية كبيرة وأرقام وشرطات سفلية، ويبدأ بحرف. '
    + 'وهو ما تُحتسب به كل التقارير، ولا يمكن تغييره بعد ذلك.',
  'requestCategory.codeTaken':
    'هذا الرمز المختصر مستخدم في هذا الفندق بالفعل. وإن لم تجده في القائمة فهو موجود لكنه '
    + 'موقوف؛ أعِد تشغيله بدل إضافته من جديد.',
  'requestCategory.promiseOutOfRange':
    'يجب أن يكون الوعد بين {min} و{max} دقيقة. لا شيء يُسلَّم في لا وقت، وما تجاوز اليوم '
    + 'فهو عمل صيانة لا طلب نزيل.',
  'requestCategory.stockItemUnknown':
    'هذا الصنف ليس من أصناف هذا الفندق، وتسليم هذا الطلب سيَعِد بحركة مخزون لا تحدث.',
  'requestCategory.lastActive':
    'هذا هو النوع الوحيد المتبقي قيد التشغيل. إيقافه سيترك الاستقبال وكل رمز استجابة في '
    + 'المبنى بلا خيار واحد.',
  'requestCategory.inUse':
    'طلبه النزلاء {requests} مرة، فلا يمكن حذفه — وستشير كل تلك الطلبات إلى شيء بلا اسم. '
    + 'أوقفه بدلًا من ذلك فيتوقف عرضه.',
  'requestCategory.alreadyInState':
    'غيّر شخص آخر هذا بالفعل. أعِد تحميل الصفحة لترى وضعه الحالي.',
  'area.closed':
    'هذه المنطقة مغلقة، فلا يمكن تسجيل تنظيف لها. '
    + 'إذا أُعيد فتحها فعلى المشرف فتحها هنا أولًا.',
  'area.service.reasonRequired':
    'اكتب السبب في جملة. المنطقة التي تختفي بهدوء من قوائم الجميع هي أصعب ما يمكن تفسيره '
    + 'لاحقًا في هذا النظام — وإعادة فتحها تحتاج السبب أكثر، لأنها تعود إلى الجدول مباشرة.',
  'area.service.unchanged':
    'هذه المنطقة في الحالة التي تريد وضعها فيها أصلًا. قد يكون شخص آخر قد سبقك — أعِد '
    + 'التحميل لترى وضعها.',
  'area.isOutOfOrder':
    'عُطل يُبقي هذه المنطقة خارج الخدمة، فليست فتحها أو إغلاقها من هنا بيدك. تعود عند '
    + 'إصلاح العُطل، وهكذا يبقى العُطل في قائمة أحدهم بدل أن يُخفى.',
  'translation.kindUnknown':
    'هذا الخادم لا يترجم هذا النوع من العناصر. أعِد تحميل الصفحة.',
  'translation.notFound':
    'لم يعد هذا السطر موجودًا — ربما حُذف وهذه الصفحة مفتوحة. أعِد التحميل لرؤية القائمة '
    + 'الحالية.',

  /* ── المفروشات والمستهلكات ─────────────────────────────────────────────── */
  'stock.item.notFound':
    'هذا الصنف لم يعد ضمن القائمة. أعد التحميل وحاول مرة أخرى.',
  'linen.quantityInvalid':
    '{value} ليست كميّة تحتاجها غرفة. استخدم رقمًا حتى ٩٩، أو أفرغ الخانة كي لا يُحمل '
    + 'منه شيء.',
  'stock.location.notFound':
    'هذا المخزن أو المستودع لم يعد معرَّفًا. راجع مدير النظام.',
  'stock.place.inUse':
    'هذا المكان مذكور في {movements} حركة مسجَّلة، فلا يمكن حذفه — كلّ واحدة منها تشير '
    + 'إليه. أوقفه بدلًا من ذلك.',
  'stock.place.lastOfKind':
    'هذا آخر مكان من نوعه. إيقافه يعني ألّا يبقى ما يُرسل منه أو يُستقبل فيه المخزون.',
  'stock.item.inUse':
    'هذا الصنف مذكور في {movements} حركة مسجَّلة، فلا يمكن حذفه — كلّ واحدة منها تشير '
    + 'إليه. أوقفه بدلًا من ذلك.',
  'stock.item.kindSettled':
    'حسمت أول حركة ما إذا كان هذا الصنف يعود، وصار عددها {movements}. وتغييره لا '
    + 'يصحّحها، بل يغيّر معنى كلّ واحدة منها ساعة تسجيلها.',
  'stock.item.inStandard':
    'هذا الصنف مذكور في {standards} معيارًا للمفروشات. أخرجه منها أولًا، كي لا يُطلب '
    + 'من أحد حمل شيء لم يعد المخزن يصرفه.',
  'shift.notFound':
    'هذا الدوام لم يعد معرَّفًا. أعد تحميل الصفحة.',
  'shift.lastActive':
    'هذا آخر دوام ما زال مُشغَّلًا. وبلا دوام لا يمكن إدراج أحد على الدوام إطلاقًا — '
    + 'فلا يوزّع مسح الصباح أي عمل وتبقى اللوحة فارغة.',
  'stock.wouldGoNegative':
    'لا يوجد سوى {available} هناك، وقد طلبت {requested}. '
    + 'إذا كان الرف يقول غير ذلك، سجّل تصحيح مخزون بدلًا من فرض العملية.',
  'stock.reasonRequired':
    'اذكر السبب قبل الشطب — فهذا يغيّر ما يُسجَّل أن الفندق يملكه.',
  'stock.movement.invalid':
    'هذه الحركة غير منطقية لعملية {kind} ({field}).',
  'stock.movement.kindUnknown':
    'أرسل هذا الإصدار من التطبيق نوع حركة لا يعرفه الخادم ({kind}). '
    + 'حدّث التطبيق قبل تسجيل هذه الحركة.',
  'auth.sessionUnknown':
    'لم يعد هذا الجهاز مسجَّل الدخول. سجّل الدخول من جديد — وسيُرسَل ما ينتظر الإرسال بعدها.',
  'auth.sessionEnded':
    'أنهى أحد المسؤولين جلسة هذا الجهاز. راجع مشرفك قبل تسجيل الدخول مرة أخرى.',

  'user.notFound':
    'هذا الشخص غير موجود في النظام.',
  'user.employeeIdTaken':
    'الرقم الوظيفي {employeeId} يخصّ شخصًا آخر. الرقم الوظيفي لا يُعاد استخدامه أبدًا — '
    + 'إعادة استخدامه تدمج سجلّي عمل شخصين نهائيًا.',
  'user.employeeIdRequired':
    'الرقم الوظيفي مطلوب، فهو وسيلة تسجيل الدخول.',
  'user.nameRequired':
    'الاسم مطلوب.',
  'user.roleNotFound':
    'هذا الدور لم يعد موجودًا.',
  'user.propertyNotFound':
    'هذا الفندق لم يعد موجودًا.',
  'user.reasonRequired':
    'اذكر السبب. تعطيل شخص يُنهي كل جلساته المفتوحة فورًا.',
  'user.wouldLockYouOut':
    'هذا سيُنهي وصولك أنت، ولا سبيل للعودة من هذه الشاشة. اطلب من مسؤول آخر تنفيذه.',
  'user.lastAdministrator':
    'هذا هو الشخص الوحيد الذي يملك {permissionCode}، وهي من الصلاحيتين اللتين لا يستغني '
    + 'عنهما الفندق. امنحها لشخص آخر أولًا، وإلا لن يتمكن أحد من معالجة مشكلات الصلاحيات.',
  'session.notFound':
    'هذه الجلسة لم تعد موجودة.',

  'permission.unknownCode':
    'أرسل هذا الإصدار من التطبيق صلاحية لا يعرفها الخادم ({permissionCode}). '
    + 'حدّث التطبيق قبل تعديل الصلاحيات.',
  'permission.grantUnknown':
    'أرسل هذا الإصدار من التطبيق إعدادًا لا يعرفه الخادم ({grant}). '
    + 'حدّث التطبيق قبل تعديل الصلاحيات.',
  'activity.filterUnknown':
    'طلب هذا الإصدار من التطبيق من سجل النشاط تصفية لا يعرفها الخادم ({filter}). '
    + 'لم يُخفَ شيء — أعد تحميل الصفحة لعرض كل شيء.',
  'permission.reasonMeaningless':
    'لا شيء في {permissionCode} يطلب سببًا من أحد، لذا فإن «مسموح مع تسجيل السبب» '
    + 'سيتصرف تمامًا مثل «مسموح» بينما يبدو أنه يفعل غير ذلك.',
  'permission.wouldLockEverybodyOut':
    'هذا سيترك النظام بلا أحد قادر على إدارته — لن يحمل {permissionCode} أي دور يشغله شخص. '
    + 'تحمله حاليًا: {heldBy}. امنحه لشخص آخر ضمن نفس الحفظ.',

  'stock.count.notFound':
    'عملية الجرد هذه لم تعد موجودة.',
  'stock.count.locationHeld':
    'الجرد {countReference} جارٍ هنا، لذا لا يمكن إدخال أو إخراج أي مخزون حتى تتم '
    + 'الموافقة عليه. هذا ما يمنع تغيّر الأرصدة أثناء عدّ الرفوف.',
  'stock.count.alreadyOpenHere':
    'الجرد {countReference} مفتوح بالفعل في هذا الموقع. أكمل ذلك الجرد بدلًا من بدء جرد ثانٍ.',
  'stock.count.wrongState':
    'حالة هذا الجرد {state}، لذا لا يمكن تنفيذ ذلك عليه الآن.',
  'stock.count.nothingCounted':
    'لم يُسجَّل أي عدد بعد. أدخل رقمًا واحدًا على الأقل قبل التسليم.',
  'stock.count.approverCounted':
    'أنت من قام بالعدّ، لذا يجب أن يعتمده شخص آخر. هذا مقصود: عدّ النقص واعتماد الشطب '
    + 'قراران منفصلان.',
  'stock.count.reasonRequired':
    'اذكر السبب. هذا يلغي عملية عدّ قام بها شخص على الرفوف.',
  'stock.count.itemNotOnSheet':
    'هذا الصنف ليس ضمن كشف الجرد. غالبًا أُخذ الكشف قبل إضافة الصنف — ابدأ جردًا جديدًا.',
  'stock.count.adjustmentUnknown':
    'أرسل هذا الإصدار من التطبيق قرارًا لا يعرفه الخادم ({adjustment}). '
    + 'حدّث التطبيق قبل اعتماد هذا الجرد.',
  'stock.count.negativeQuantity':
    'لا يمكن أن يحتوي الرف على أقل من لا شيء. إذا لم يوجد أي منه، سجّله صفرًا.',

  'stock.adjustment.needsApproval':
    'تصحيح رصيد الرف يحتاج إلى اعتماد شخص آخر قبل أن يُحتسب. أرسله للاعتماد، وسيراه من '
    + 'لديه صلاحية المخزون.',
  'stock.adjustment.notFound':
    'هذا التصحيح لم يعد موجودًا. ربما بتّ فيه أحدهم بينما كنت تنظر إليه.',
  'stock.adjustment.alreadyDecided':
    'سبق البتّ في هذا ({state}). ولم يتغيّر شيء مرة ثانية.',
  'stock.adjustment.approverRaised':
    'أنت من طلب هذا التصحيح، لذا يجب أن يبتّ فيه شخص آخر. هذا مقصود: القول بأن الرصيد '
    + 'خاطئ واعتماد التصحيح قراران منفصلان.',
  'stock.adjustment.noteRequired':
    'اذكر سبب الرفض. فقد عدّ أحدهم شيئًا وهو يستحقّ جملة.',
  'stock.adjustment.zeroQuantity':
    'تصحيح بلا مقدار ليس تصحيحًا. أدخل المقدار الذي يجب أن يتغيّر به الرصيد.',

  'work.trigger.switchedOff':
    'أوقف هذا الفندق ذلك، فلم يعد يُنشأ عمل من هذا النوع ({trigger}). ويستطيع مسؤول '
    + 'النظام إعادة تشغيله من الإعدادات.',
  'work.trigger.unknown':
    'أرسل هذا الإصدار من التطبيق مُشغِّلًا لا يعرفه الخادم ({trigger}). حدّث التطبيق.',
  'work.trigger.intervalRequired':
    'حدّد عدد الأيام أولًا. فتجديد الغرفة يحتاج أن يعرف كم يجوز أن تبقى الغرفة المرتَّبة '
    + 'قبل أن يمرّ عليها أحد من جديد — وهذا لا يعرفه إلا هذا الفندق.',
  'work.trigger.intervalOutOfRange':
    'هذه ليست وتيرة ({days} يومًا). من يوم واحد إلى سنة.',

  'validation.failed':
    'هناك خطأ في أحد الحقول ({field}).',

  'permission.denied':
    'لا يسمح دورك بهذا الإجراء. يمكن لمشرفك طلب الصلاحية نيابةً عنك.',
  'permission.reasonRequired':
    'يمكنك القيام بهذا، لكن يجب تسجيله مع ذكر السبب.',

  'sync.lateTransitionConflict':
    'تغيّرت حالة الغرفة {roomNumber} أثناء انقطاع اتصال الجهاز، لذلك لم يُطبَّق التحديث المؤجَّل. تحقّق من الغرفة وسجّل الحالة من جديد.',
  'sync.idempotencyKeyReusedWithDifferentPayload':
    'أرسل هذا الجهاز تحديثين مختلفين بالمرجع نفسه. لم يتم تغيير أي شيء. يُرجى إبلاغ الدعم.',
}

export const CATALOGUES: Record<AppLocale, Catalogue> = { en, ar }

/** Shown when the server was never reached. Deliberately not in the code catalogue:
 *  there is no code, because nothing on the server formed an opinion. */
const TRANSPORT: Record<AppLocale, string> = {
  en: 'The server could not be reached. Your work is safe; this screen will update when the connection returns.',
  ar: 'تعذّر الوصول إلى الخادم. عملك محفوظ، وستُحدَّث هذه الشاشة عند عودة الاتصال.',
}

/** Last resort for a code this build has never heard of — a server newer than this
 *  client. It is a SENTENCE, not the raw identifier: showing "room.transition.
 *  notPermitted" to a housekeeper is not an error message, it is a stack trace. */
const UNKNOWN: Record<AppLocale, string> = {
  en: 'Something went wrong and this screen could not describe it. Please quote the reference below to support.',
  ar: 'حدث خطأ تعذّر على هذه الشاشة وصفه. يُرجى ذكر الرقم المرجعي أدناه عند التواصل مع الدعم.',
}

function interpolate(template: string, details: Record<string, string>): string {
  // An unfilled placeholder is left as-is rather than blanked: "Room {roomNumber}"
  // reads as a bug to whoever sees it, whereas "Room " reads as missing data and gets
  // reported as a data problem instead of a translation one.
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => details[key] ?? whole)
}

export interface TranslatedError {
  message: string
  /** Quoted in small print so a support call starts with a reference. */
  correlationId?: string
  /** True when the failure was the network, so a screen can show the offline state. */
  isTransport: boolean
}

export function translateError(error: unknown, locale: AppLocale): TranslatedError {
  if (error instanceof TransportError) {
    return { message: TRANSPORT[locale], isTransport: true }
  }

  if (error instanceof ApiError) {
    const template = CATALOGUES[locale][error.code]
    return {
      message: template ? interpolate(template, error.details) : UNKNOWN[locale],
      correlationId: error.correlationId,
      isTransport: false,
    }
  }

  return { message: UNKNOWN[locale], isTransport: false }
}
